import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AdminToken } from "@/lib/types";
import { Panel } from "@/ui/Panel";
import { Filter } from "@/ui/Filter";
import { Chip } from "@/ui/Chip";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, TableSkeleton } from "@/ui/States";
import { useFilters } from "@/features/admin/useFilters";
import { absoluteTime, shortTime } from "@/lib/format";

export const Route = createFileRoute("/_app/admin/tokens")({
  component: TokensScreen,
});

/**
 * Every API token on the installation.
 *
 * # WHY
 *
 * A leaked token is an incident, and the first question is always the same:
 * what else could that credential reach. Staff could not enumerate tokens at
 * all, so the answer was psql or nothing.
 *
 * # WHAT IS NOT HERE
 *
 * The token, and its hash. Neither is needed to answer any question this screen
 * exists for, and a staff surface that returns credential material is a surface
 * somebody will eventually screenshot into a support thread. The prefix is what
 * a customer sees in their own list and is enough to match a token against one
 * in a log.
 */
function TokensScreen() {
  const filters = useFilters();
  const includeRevoked = filters.on("revoked");
  const setIncludeRevoked = (value: boolean) => filters.toggle("revoked", value);

  const tokens = useQuery({
    queryKey: ["platform-tokens", includeRevoked],
    queryFn: ({ signal }) =>
      api.get<{ tokens: AdminToken[] | null }>(
        "/admin/tokens",
        { include_revoked: includeRevoked || undefined, limit: 300 },
        signal,
      ),
  });

  const list = tokens.data?.tokens ?? [];
  // A token nobody has used in three months is a credential nobody would miss
  // and nobody is watching, which is the one worth asking about.
  const idle = list.filter((t) => !t.revoked_at && dormant(t));

  return (
    <div className="flex flex-col gap-4">
      {idle.length > 0 && (
        <div className="rounded-xl border border-[color-mix(in_oklab,_var(--warning)_35%,_transparent)] bg-[color-mix(in_oklab,_var(--warning)_12%,_transparent)] px-4 py-3">
          <p className="text-[14px] font-medium text-[var(--warning)]">
            {idle.length === 1
              ? "One live token has not been used in 90 days"
              : `${idle.length} live tokens have not been used in 90 days`}
          </p>
          <p className="mt-0.5 text-[13px] text-[var(--text-secondary)]">
            A credential nobody would miss is a credential nobody is watching.
          </p>
        </div>
      )}

      <Panel
        toolbar={
          <Filter
            label="Include revoked"
            active={includeRevoked}
            onClick={() => setIncludeRevoked(!includeRevoked)}
          />
        }
        padded={false}
      >
        {tokens.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={5} columns={6} />
          </div>
        ) : tokens.isError ? (
          <div className="px-5 py-4">
            <ErrorState error={tokens.error} onRetry={() => void tokens.refetch()} />
          </div>
        ) : list.length === 0 ? (
          <div className="px-5 py-8">
            <EmptyState title="No tokens" description="Nobody has created one." />
          </div>
        ) : (
          <div className="px-5 py-3">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[20%]">Name</Th>
                  <Th width="w-[14%]">Prefix</Th>
                  <Th width="w-[18%]">Tenant</Th>
                  <Th width="w-[18%]">Created by</Th>
                  <Th width="w-[10%]">Scope</Th>
                  <Th width="w-[11%]" align="right">
                    Last used
                  </Th>
                  <Th width="w-[9%]" align="right">
                    Expires
                  </Th>
                </Tr>
              </thead>
              <tbody>
                {list.map((t) => (
                  <Tr key={t.id} className={t.revoked_at ? "opacity-60" : undefined}>
                    <Td clip title={t.name}>
                      {t.name}
                      {t.revoked_at && (
                        <Chip  className="ml-1.5">
                          revoked
                        </Chip>
                      )}
                    </Td>
                    <Td clip className="mono text-[13px]" title={t.prefix}>
                      {t.prefix}
                    </Td>
                    <Td clip title={t.organisation_name}>
                      <Link
                        to="/admin/$orgId"
                        params={{ orgId: t.organisation_id }}
                        className="hover:text-[var(--primary)]"
                      >
                        {t.organisation_name}
                      </Link>
                    </Td>
                    <Td clip title={t.owner_email || undefined}>
                      {t.owner_id ? (
                        <Link
                          to="/admin/users/$userId"
                          params={{ userId: t.owner_id }}
                          className="hover:text-[var(--primary)]"
                        >
                          {t.owner_email || t.owner_id}
                        </Link>
                      ) : (
                        <span className="text-[var(--text-muted)]">—</span>
                      )}
                    </Td>
                    <Td clip title={t.project_id || t.scope}>
                      {t.scope}
                    </Td>
                    <Td
                      align="right"
                      title={t.last_used_at ? absoluteTime(t.last_used_at) : undefined}
                    >
                      {t.last_used_at ? (
                        <span className={dormant(t) ? "text-[var(--warning)]" : undefined}>
                          {shortTime(t.last_used_at)}
                        </span>
                      ) : (
                        <span className="text-[var(--text-muted)]">never</span>
                      )}
                    </Td>
                    <Td align="right" title={t.expires_at ? absoluteTime(t.expires_at) : undefined}>
                      {t.expires_at ? shortTime(t.expires_at) : "never"}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>
    </div>
  );
}

/** Not used in ninety days, counting from creation when it never was. */
function dormant(t: AdminToken): boolean {
  const last = new Date(t.last_used_at ?? t.created_at).getTime();
  if (!Number.isFinite(last)) return false;
  return Date.now() - last > 90 * 86400000;
}
